import type { StreamTaskStatus } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { TaskProgressState } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import type { ChatStoreAction } from './types';

type HandleStreamTaskStatusParams = {
	conversationId: string;
	taskMessage: StreamTaskStatus['message'];
	messageId?: string;
	createTaskFromStream: (args: {
		conversationId: string;
		taskId: string;
		title: string;
		updatedAt?: string;
		messageId?: string;
	}) => ChatStoreAction;
	setTaskProgressState: (args: {
		conversationId: string;
		progressState: TaskProgressState;
		title?: string;
		updatedAt?: string;
		seekingCompletedAck?: boolean;
		messageId?: string;
		completionMessageId?: string;
		taskId?: string;
	}) => ChatStoreAction;
	dispatch: (action: ChatStoreAction) => void;
};

/**
 * Shared handler for TASK_STATUS stream messages.
 *
 * Maps the API task status to the appropriate store action and dispatches it.
 * Used by both `sendMessage` (via `processStream`) and `sendMessageConfirmedTools`
 * to avoid duplicating the status → action mapping logic.
 */
export const handleStreamTaskStatus = ({
	conversationId,
	taskMessage,
	messageId,
	createTaskFromStream,
	setTaskProgressState,
	dispatch,
}: HandleStreamTaskStatusParams): void => {
	switch (taskMessage.status) {
		case 'DRAFT':
			dispatch(
				createTaskFromStream({
					conversationId,
					taskId: taskMessage.task_id,
					title: taskMessage.title,
					updatedAt: taskMessage.updated_at,
					messageId,
				}),
			);
			break;
		case 'COMPLETED':
			dispatch(
				setTaskProgressState({
					conversationId,
					progressState: 'Completed',
					title: taskMessage.title,
					updatedAt: taskMessage.updated_at,
					seekingCompletedAck: true,
					completionMessageId: messageId,
				}),
			);
			break;
		case 'FAILED':
			dispatch(
				setTaskProgressState({
					conversationId,
					progressState: 'Failed',
					title: taskMessage.title,
					updatedAt: taskMessage.updated_at,
					messageId,
				}),
			);
			break;
		case 'CANCELLED':
			dispatch(
				setTaskProgressState({
					conversationId,
					progressState: 'Cancelled',
					title: taskMessage.title,
					updatedAt: taskMessage.updated_at,
					messageId,
				}),
			);
			break;
		default:
			break;
	}
};
